<template>
  <form @submit.prevent="submitForm" class="pa-form">
    <!-- Order summary -->
    <div class="pa-summary">
      <div class="sum-grid">
        <div class="sum-row">
          <span class="sum-label">Receipt</span>
          <span class="sum-value mono upper">{{ item.receiptId }}</span>
        </div>
        <div class="sum-row">
          <span class="sum-label">Order ID</span>
          <span class="sum-value mono break">{{ item.merchantOrderId }}</span>
        </div>
        <div class="sum-row">
          <span class="sum-label">Customer</span>
          <span class="sum-value">{{ item.customerName || '—' }}</span>
        </div>
        <div class="sum-row">
          <span class="sum-label">Assigned bank</span>
          <span class="sum-value">{{ item.validatorUsername || '—' }}</span>
        </div>
        <div v-if="item.customerUPIID" class="sum-row">
          <span class="sum-label">Client UPI</span>
          <span class="sum-value mono">{{ item.customerUPIID }}</span>
        </div>
        <div v-if="isAdmin && item.validatorUPIID" class="sum-row">
          <span class="sum-label">Assigned UPI</span>
          <span class="sum-value mono break">{{ item.validatorUPIID }}</span>
        </div>
        <div v-if="item.customerUtr" class="sum-row">
          <span class="sum-label">Customer UTR</span>
          <span class="sum-value mono">{{ item.customerUtr }}</span>
        </div>
        <div class="sum-row">
          <span class="sum-label">Created</span>
          <span class="sum-value">{{ formatDate }}</span>
        </div>
      </div>
      <div class="pa-amount-block">
        <div class="amount-label">Amount to approve</div>
        <div class="amount-value">₹{{ formatMoney(getAmount) }}</div>
      </div>
    </div>

    <!-- UTR input -->
    <div class="field" :class="{ 'field-invalid': !transactionID.isValid }">
      <label>Bank UTR <span class="req">*</span></label>
      <div class="input-wrap">
        <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <rect x="3" y="6" width="18" height="12" rx="2" />
          <path d="M3 10h18M7 15h4" stroke-linecap="round" />
        </svg>
        <input
          type="text"
          id="transaction-id"
          inputmode="numeric"
          pattern="[0-9]*"
          maxlength="22"
          v-model.trim="transactionID.val"
          placeholder="Enter UTR (digits only)"
          class="input mono has-icon"
          @input="filterUtrInput($event, 'transactionID')"
          @focus="clearValidity('transactionID')"
        />
      </div>
      <p v-if="!transactionID.isValid" class="field-error">UTR code must not be empty.</p>
    </div>

    <div class="form-actions">
      <button
        type="submit"
        class="btn btn-success"
        :disabled="isLoading"
      >
        <span v-if="!isLoading" class="btn-content">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
            <path d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          Confirm approval
        </span>
        <span v-else class="btn-loading">
          <span class="spinner-mini"></span>
          Confirming…
        </span>
      </button>
    </div>
  </form>
</template>

<script>
export default {
  name: "pay-inapprove-form",
  props: ["item"],
  emits: ["form-response"],
  data() {
    return {
      transactionID: {
        val: "",
        isValid: true,
        msg: "",
      },
      formIsValid: true,
      isLoading: false,
    };
  },
  computed: {
    getAmount() {
      if (this.item.amount) {
        if (this.item.txnFee) {
          return this.item.amount;
        } else {
          return parseInt(this.item.amount);
        }
      } else {
        return "";
      }
    },
    isAdmin() {
      return !!this.$store.getters.isAdmin;
    },
    formatDate() {
      if (!this.item.createdAt) return "";
      const date = new Date(this.item.createdAt);
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const seconds = date.getSeconds();
      const ampm = hours >= 12 ? "pm" : "am";
      return `${day}-${month}-${year} ${hours % 12}:${minutes}:${seconds} ${ampm}`;
    },
  },
  methods: {
    clearValidity(input) {
      this[input].isValid = true;
      this[input].msg = "";
    },
    formatMoney(v) {
      const n = Number(v) || 0;
      return n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
    // Strip everything that isn't an ASCII digit as the operator types
    // or pastes. Mirrors the customer-side filter on the payment page.
    // Closes the Unicode-lookalike + special-character attack vectors
    // at the UI level before they reach normalizeUtr on the backend.
    filterUtrInput(event, fieldKey) {
      const raw = event.target.value;
      const cleaned = raw.replace(/[^0-9]/g, '');
      if (cleaned !== raw) {
        event.target.value = cleaned;
        this[fieldKey].val = cleaned;
      }
    },
    validateForm() {
      this.formIsValid = true;
      // Belt-and-suspenders: re-strip on the bound value just before
      // validation, catching any programmatic write (autofill / dev
      // console) that bypassed @input.
      this.transactionID.val = String(this.transactionID.val || '').replace(/[^0-9]/g, '');
      if (this.transactionID.val === "") {
        this.transactionID.isValid = false;
        this.formIsValid = false;
      }
    },
    async submitForm() {
      this.validateForm();

      if (!this.formIsValid) {
        return;
      }

      const formData = {
        refID: this.item.refID,
        transactionID: this.transactionID.val,
      };

      this.isLoading = true;

      try {
        await this.$store.dispatch("payin/approveOrder", formData);
        this.transactionID = { val: "", isValid: true, msg: "" };
        this.$emit("form-response", "success", "Order approved successfully.");
      } catch (error) {
        this.error = error.message || "Something failed!";
        this.$emit("form-response", "failed", this.error);
      }

      this.isLoading = false;
    },
  },
};
</script>

<style scoped>
.pa-form { display: flex; flex-direction: column; gap: 14px; }

.pa-summary {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 14px;
  padding: 14px;
  background: #fbfaf7;
  border: 1px solid #ede9df;
  border-radius: 12px;
  align-items: center;
}
.sum-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 20px;
  min-width: 0;
}
.sum-row {
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 8px;
  align-items: baseline;
  font-size: 12.5px;
  min-width: 0;
}
.sum-label { color: #94a3b8; font-weight: 500; }
.sum-value { color: #1b2a47; font-weight: 600; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
.sum-value.mono { font-family: "SFMono-Regular", Menlo, monospace; font-size: 11.5px; color: #334155; letter-spacing: 0.02em; }
.sum-value.upper { text-transform: uppercase; }
.sum-value.break { word-break: break-all; overflow-wrap: anywhere; }

.pa-amount-block {
  padding: 10px 16px;
  background: linear-gradient(135deg, #059669, #047857);
  border-radius: 12px;
  color: #fff;
  text-align: right;
  box-shadow: 0 6px 16px -8px rgba(5, 150, 105, 0.55);
  min-width: 160px;
}
.amount-label {
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.65);
  font-weight: 700;
}
.amount-value {
  font-size: 22px;
  font-weight: 800;
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  line-height: 1.1;
}

.field { display: flex; flex-direction: column; }
.field label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
}
.req { color: #dc2626; }
.input-wrap { position: relative; display: flex; align-items: center; }
.input-icon {
  position: absolute;
  left: 12px;
  width: 16px; height: 16px;
  color: #94a3b8;
  pointer-events: none;
}
.input {
  width: 100%;
  padding: 12px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  background: #fbfaf7;
  color: #0f172a;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  letter-spacing: 0.05em;
}
.input.has-icon { padding-left: 38px; }
.input.mono { font-family: "SFMono-Regular", Menlo, monospace; }
.input:focus {
  border-color: #059669;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(5, 150, 105, 0.10);
}
.field-invalid .input {
  border-color: #fca5a5;
  background: #fef2f2;
}
.field-error {
  font-size: 12px;
  color: #dc2626;
  margin: 6px 0 0;
  font-weight: 500;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 4px;
}
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 11px 22px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  outline: none;
  font-family: inherit;
  letter-spacing: 0.02em;
}
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn-success {
  background: linear-gradient(135deg, #10b981, #059669);
  color: #fff;
  box-shadow: 0 8px 18px -8px rgba(5, 150, 105, 0.55);
  min-width: 200px;
}
.btn-success:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 12px 24px -10px rgba(5, 150, 105, 0.65);
}
.btn-content { display: inline-flex; align-items: center; gap: 6px; }
.btn-content svg { width: 15px; height: 15px; }
.btn-loading { display: inline-flex; align-items: center; gap: 8px; }
.spinner-mini {
  width: 12px; height: 12px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 640px) {
  .pa-summary { grid-template-columns: 1fr; }
  .sum-grid { grid-template-columns: 1fr; }
  .pa-amount-block { text-align: center; min-width: 0; }
}
</style>
